import { EsEvent } from "@app2/search/events-page/type-defs";
import { SlugName, TicketState, TicketWatcherType } from "@app2/ticket/ticket-constants";
import { TicketFile } from "@app2/type-defs/ticket/ticket-files.types";

export interface TicketLog {
    id?: uuid;
    oldVersion?: number;
    oldValues: TicketLogValues;
    newValues: TicketLogValues;
    changedBy: uuid;
    changedOn: string;
    reason?: string;
    notes: TicketNote[];
}

export interface TicketEventGroup {
    type: string;
    begin: date;
    end: date;
    customHeaders: string[];
    events: object[];
}

export interface TicketLogValues {
    links?: TicketLink[];
    searches?: TicketSearch[];
    // Never in new ticket logs
    workflowData?: any;
    // Only exists in pre-workflowData removal ticket logs, still needs to be supported
    alerts?: uuid[];
    alertIds?: uuid[];
    eventGroups?: {
        custom: {
            type: string,
            events: EsEvent[],
        },
    };
    policies?: uuid[];
    watchers?: TicketWatcher[];
    state?: string;
    ownedBy?: uuid;
    description?: string;
    active?: boolean;
    dueOn?: string;
    taskData?: any;
    files?: TicketFile[];
    historicalSearches?: TicketSearch[];
    lineItemIds?: uuid[];
    subtasks?: any[];
    severity?: string;
    threatMatch?: boolean;
    logs?: any[];
    summary?: string;
    // Only exists in pre-workflowData removal ticket logs, still needs to be supported
    resolution?: TicketResolution;
    resolutionKey?: string;
    resolutionDetail?: string;
    timeZoneId?: uuid;
    slugId?: number;
    notifiedRegulator?: boolean;

    externalTicketId?: string;
}


export interface TicketLogNote {
    id?: uuid;
    ticketLogId?: uuid;
    createdBy?: uuid;
    createdOn?: string;
    modifiedOn?: string;
    noteText: string;
}

export interface Ticket extends TicketSummary {
    id?: string;
    watchers: TicketWatcher[];
    searches: Array<Partial<TicketSearch>>;
    links: TicketLink[];
    // Deprecated
    workflowData?: WorkflowData;
    resolution?: TicketResolution; // Used in legacy console code
    resolutionKey?: string; // Used in ticket-service POJO
    resolutionDetail?: string; // Used in ticket-service POJO
    threatMatch?: boolean;
    trigger?: uuid;
    severity?: string;
    alerts?: uuid[]; // Used within console previously
    alertIds?: uuid[]; // Used in ticket-service POJO
    eventGroups?: Record<string, TicketEventGroup>;
    description?: string;
    files?: any[];
    lineItemIds: uuid[];
    riskControlIds: uuid[];
    dueOn?: string; // iso string
    timeZoneId?: any;
    previousTaskSlugId: any;
    nextTaskSlugId: any;
    subtasks?: Ticket[];
    // Tasks created before ticket-service-1.58 (December 10th, 2019) will not have this.
    // Incidents should never have this.
    taskData?: Partial<TaskData>;
    closedOn?: string; //iso string
    logCount?: number;
    logs?: TicketLog[];
    notifiedRegulator?: boolean;
    tagIds: uuid[];
    externalTicketId?: string;
}

export interface TicketLink {
    type: string;
    reverse: boolean;
    slugName: SlugName;
    slugId: number;
    ticketSummary?: string;
}

export interface TicketSummary {
    orgId?: uuid;
    slugName: SlugName;
    slugId?: number;
    active?: boolean;
    version?: number;
    summary?: string;
    state?: TicketState;
    autoGenerated?: boolean;

    createdBy?: uuid;
    createdOn?: string; // iso string
    updatedBy?: uuid;
    updatedOn?: string; // iso string
    ownedBy?: uuid;
    slaDueDate?: date;
    tracClaimedOn?: date;
    tracClaimedBy?: uuid;
}

export interface TicketWatcher {
    personId: uuid;
    type: TicketWatcherType;
}

export interface TicketTemplate {
    id: uuid;
    name: string;
    body: string;
}

export interface TicketSearch {
    id: uuid;
    ticketId: uuid;
    description: string;
    query: TicketSearchQuery;
    createdOn: date;
    active: boolean;
    customColumns: string[];
    searchState: "PENDING" | "COMPLETED" | "ERROR";
    events: any[];
    totalCount: number;
}

export interface TicketSearchQuery {
    queryString: string;
    begin: string;
    end: string;
    primaryField?: string;
    secondaryField?: string;
}

export interface TicketResolution {
    key: string; // TODO: enum?
    detail?: string;
}

export interface WorkflowData {
    resolution?: TicketResolution | null;
    trigger?: string;
    severity?: string;
    alerts?: uuid[];
    taskSchedule?: uuid;
}

export type TicketSortField = "SLUG" | "SUMMARY" | "CREATED_ON" | "UPDATED_ON" | "DUE_ON";

export interface TicketPageRequest {
    filters: Partial<TicketPageFilters>;
    pageStart?: TicketSummary;
    pageSize: number;
    pageNumber?: number;
    paginationRange?: number;
    sort: TicketSortField;
    descending: boolean;
    includeDeleted: boolean;
    completeTickets: boolean;
    numLogs: number;
    includedElements: string[]; //TODO: TicketElement
}

export interface TicketPagesResponse {
    ticketSummaries: Record<number, TicketSummary>;
    totalCount: number;
}

export interface HierarchicalTaskPageResponse {
    tasks: Ticket[];
    matchedSlugIds: number[];
}

export interface TicketFilter {
    field: string;
    filter: any;
}

export const TicketElements = Object.freeze({
    links: "links",
    watchers: "watchers",
    files: "files",
    searches: "searches",
    logs: "logs",
    description: "description",
    workflowData: "workflowData",
    taskData: "taskData",
    nextAndPreviousTasks: "nextAndPreviousTasks",
    historicalSearches: "historicalSearches",
});
export const defaultTicketElements = [TicketElements.links, TicketElements.watchers, TicketElements.files,
    TicketElements.searches, TicketElements.logs, TicketElements.description, TicketElements.workflowData,
    TicketElements.taskData, TicketElements.nextAndPreviousTasks];

export interface TicketPageFilters {
    owner: uuid;
    owners: uuid[];
    creator: uuid;
    creators: uuid[];
    lineItemIds: uuid[];
    riskControlIds: uuid[];
    severity: string;
    severities: string[];
    state: string[];
    search: string;
    before: string;
    after: string;
    dueBefore: string;
    dueAfter: string;
    hasDueDate: boolean;
    taskScheduleId: uuid;
    parentSlugId: number;
    isTopLevelTask: boolean;
    triggerId: uuid;
    org: uuid;
    tagIds: uuid[];
    isTracContactOwned: boolean;
}

export interface TaskData {
    taskScheduleId: uuid | null;
    parentSlugId: number | null;
    summaryNote: string | null;
    closedException: boolean;
}

export interface TicketNote {
    id?: uuid;
    ticketLogId?: string | null;
    createdBy?: uuid;
    createdOn?: date;
    modifiedOn?: date;
    noteText: string;
}

export interface SlugIdVersion {
    slugId: number;
    version: number;
}

export interface BulkCloseResponse {
    failed: number;
    success: number;
}

export interface TicketEscalationSettings {
    id: uuid;
    orgId: uuid;

    currentOwnerWatcherType: TicketWatcherType;
    tracEngineerWatcherType: TicketWatcherType;

    escalationOwnerId: uuid;
    escalationWatchers: TicketWatcher[];

    createdOn: date;
    modifiedOn: date;
    modifiedBy: uuid;
}

export interface WorkflowStats {
    ticketsClosed: number;
    ticketsOpened: number;
}
